/*
 * List of vendor resale commission plans assigned to a reseller. Conceptually there should only be one
 * currently active plan, but there can be subscription specific plans, and old plans should be preserved 
 * for audit purposes / reporting purposes.
 */
import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import { Redirect } from 'react-router-dom';
import AbEnvironment from '../auth/AbEnvironment';
import withStyles from '@mui/styles/withStyles';
import { Grid, Link, Paper, TableContainer, Table, TableHead, TableCell, TableBody, TablePagination, TableRow, Typography } from '@mui/material';

import CheckCircle from '@mui/icons-material/CheckCircle';
import PauseCircleFilled from '@mui/icons-material/PauseCircleFilled';

import LoadingSpinner from '../ui/LoadingSpinner';
import ServerError from '../ui/ServerError';
import SaveButton from '../ui/SaveButton';
import AppBindStyles from '../../AppBindStyles';
import ResellerCommProgramSelect from './ResellerCommProgramSelect';
import ResellerCommProgramCreateMutation from './ResellerCommProgramCreateMutation';

import { green, red } from '@mui/material/colors';

const abEnvironment = new AbEnvironment();
const ResellerCommProgramsQuery = graphql`
query ResellerCommProgramsQuery($resellerAccountId: UUID!, $first: Int, $offset: Int) {
  allResellerProgramTs(condition: {resellerAccountId: $resellerAccountId}, orderBy:PROGRAM_NAME_ASC, first: $first, offset: $offset) {
    totalCount
    nodes {
      resellerPId
      cpId
      description
      programType
      programName
      qualificationPeriod
      notes
      active
      customerAppId
      resellerProgramLineTsByResellerPId {
        totalCount
        nodes {
          revenueMin
          incentivePerc
          
        }
      }
      customerAppTByCustomerAppId {
        accountTByCustomerAccountId {
          accountName
        }
      }
      cpTByCpId {
        programName
        programType
        qualificationPeriod
        cpLineTsByCpId {
          totalCount
          nodes {
            revenueMin
            incentivePerc

          }      
        }  
      }
    }
  }
}
`;

const styleSheet = theme => ({
  root: {
    flexGrow: 1,
    //marginTop: 10,
  },
  flex: {
    flex: 1,
  },
  button: {
    marginTop: 10
  },
  paper: {
    maxWidth: 1200,
    marginTop: theme.spacing(2),
  },
  tblHead: {
    backgroundColor: '#f1f1f1',
  },
});

class ResellerCommPrograms extends Component {
  state = {
    // State attributes for assigning a plan
    cpId:                 null,
    programType:          null,
    programName:          null,
    description:          null,
    qualification_period: null,

    searchP: '',
    page: 0,
    rowsPerPage: 10,

    showSpinner:          false,
    saveStatus:           null,
    savedClicked:         false,
    saveMessage:          null,
    saveSnackbarOpen:     false,
    saveSnackbarDuration: null
};

  goTo(route) {
    this.props.history.push(`/${route}`);
  }

  handleChange = name => event => {
    this.setState({
      [name]: event.target.value,
    });
  };
  
  handleChangePage = (event, page) => {
    this.setState({page: page});
  }
  
  handleChangeRowsPerPage = (event) => {
    this.setState({ rowsPerPage: event.target.value, page: 0});
  }

  setProgramIdState = (sValue) => {
    // Set reseller program attributes based on program template that is selected.
    let aValue = JSON.parse(sValue);
    this.setState(
      {
        cpId:                aValue.cpId,
        programType:         aValue.programType,
        programName:         aValue.programName,
        description:         aValue.description,
        qualificationPeriod: aValue.qualificationPeriod,
      }
    );
  };

  render() {
    const { isAuthenticated, isAuthenticatedVendor, needAssent, logError } = this.props.auth;
    const classes = this.props.classes;

    const statusOk = green[300];
    const statusInactive = red[300];

    // Determine the offset
    let offset = 0;
    if (this.state.page > 0)
    {
      offset = this.state.page * this.state.rowsPerPage
    }
    let first = this.state.rowsPerPage;

    let totalCount = 0;

    if (!isAuthenticated() || needAssent()) {
      return ( <Redirect to='/home' /> );
    }

    if (isAuthenticatedVendor())
    {
      let resellerAccountId = this.props.resellerAccountId;
      //let createLink = 'resellerprogramcreate?aId=' + resellerAccountId;
 
      // Need vendor account id to lookup templates
      let vendorAccountId = abEnvironment.getUserAttribute('a_id');

      return (
        <div className={classes.root}>
          <QueryRenderer
            environment={abEnvironment.getEnvironment()}
            query={ResellerCommProgramsQuery}
            variables={{resellerAccountId: resellerAccountId, 
                        first: first,
                        offset: offset}}
            render={({error, props})=> {
              if (error) {
                logError(error.message);
                return (
                  <ServerError/>
                );
              } else if (props) {
                // Determine offset information
                totalCount = props.allResellerProgramTs.totalCount;
                let rPlans = props.allResellerProgramTs.nodes;

                let assignDisabled = false;
                if (this.state.cpId == null) {
                  assignDisabled = true;
                }

                let assignVariant = 'contained';
                if (totalCount > 0) {
                  assignVariant = 'outlined';
                }

                let theResellerName = this.props.resellerName;
                if (theResellerName == null) {
                  theResellerName = "this reseller";
                }

                // Prep already assigned array
                let alreadyAssigned = [];
                
                // Add the cp_id for each program that is already assigned, unless this is specific to a customer
                for (let i=0; i<rPlans.length; i++) {
                  let aPlan = rPlans[i];
                  if (aPlan.customerAppId == null) {
                    alreadyAssigned.push(aPlan.cpId);
                  }
                }

                return (
                  <React.Fragment>
                    <Grid container spacing={2} direction="row" alignItems="flex-end">
                      <Grid item xs={12}>
                        <Typography variant="h6">
                          Manage commissions for {theResellerName}
                        </Typography>
                      </Grid>
                      {/*
                      <Grid item xs={12}>
                        <Grid container spacing={0}>
                          <Grid item xs={12}>
                            <Typography variant="h6">
                              Manage commission programs for {theResellerName}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="body2">Assign and customize reseller programs</Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                      */}

                      {/* selection and assignment */}
                      <Grid item>
                        <ResellerCommProgramSelect
                          vendorAccountId={vendorAccountId}
                          showRequired={false}
                          cpId={this.state.cpId}
                          stage={this.props.stage}
                          setProgramIdState={this.setProgramIdState}
                          widthOverride='340px'
                          alreadyAssigned={alreadyAssigned}
                        />
                      </Grid>
                      <Grid item>
                        <SaveButton
                          variant={assignVariant}
                          disabled={assignDisabled}
                          buttonStyle="none"
                          snackbarOpen={this.state.saveSnackbarOpen}
                          autoHideDuration={this.state.saveSnackbarDuration}
                          label="Assign plan"
                          status={this.state.saveStatus} 
                          message={this.state.saveMessage}
                          onSaveClick={() => { this.createResellerProgram(vendorAccountId, resellerAccountId); }}
                          onSnackbarClose={() => { this.onSnackbarClose(); }}
                        />
                      </Grid>

                      {/* assignment on a different page */}
                      {/*
                          style={{marginTop: 14}}
                      <Grid item xs={12}>
                        <Button 
                          color="primary"
                          variant={assignVariant}
                          disabled={assignDisabled}
                          className={classes.button}
                          onClick={() => {this.goTo(createLink);}}>
                          <Add/> Assign plan
                        </Button>
                      </Grid>
                      */}
                    </Grid>
                    <div className={classes.paper}>
                      <Paper >
                        <TableContainer>
                          <Table>
                            <TableHead className={classes.tblHead}>
                              <TableRow>
                                <TableCell>Plan</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Customized</TableCell>
                                <TableCell align="center">Status</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {rPlans.map(n => {
                                //now we need to determine if the plan is customized - meaning it is different from the vendors.
                                let programCustomized = false;
                                //easiest thing is to check line count
                                if (n.resellerProgramLineTsByResellerPId.totalCount !== n.cpTByCpId.cpLineTsByCpId.totalCount) {
                                  programCustomized = true;
                                }
                                if (programCustomized === false) {
                                  //we now know lines are the same, we need to brute force compare the lines.
                                  let i = 0;
                                  for (i = 0; i < n.resellerProgramLineTsByResellerPId.nodes.length; i++) {
                                    let resellerLine = n.resellerProgramLineTsByResellerPId.nodes[i];
                                    //we can do this as we already discerned above line count is the same.
                                    let vendorLine = n.cpTByCpId.cpLineTsByCpId.nodes[i];
                                    if (resellerLine.revenueMin !== vendorLine.revenueMin) {
                                      programCustomized = true;
                                      break;
                                    }
                                    if (resellerLine.incentivePerc !== vendorLine.incentivePerc) {
                                      programCustomized = true;
                                      break;
                                    }
                                  }
                                }

                                if (programCustomized === false) {
                                  //lets just a couple other things
                                  if (n.programType !== n.cpTByCpId.programType) {
                                    programCustomized = true;
                                  }
                                  if (n.qualificationPeriod !== n.cpTByCpId.qualificationPeriod) {
                                    programCustomized = true;
                                  }
                                }

                                let customizedText = 'No';
                                if (programCustomized) {
                                  customizedText = 'Yes';
                                }


                                let theUrl = "resellerprogramedit?resellerPId=" + n.resellerPId + "&accountId=" + resellerAccountId + '&fp=Partner';
                                if (n.customerAppId !== null && n.customerAppId !== "") {
                                  customizedText = customizedText + ' (' + n.customerAppTByCustomerAppId.accountTByCustomerAccountId.accountName + ' only)';
                                }
                
                                return (
                                  <TableRow key={n.resellerPId}>
                                    <TableCell>
                                      <Link component="button" 
                                            variant="body2"
                                            onClick={this.goTo.bind(this, theUrl)}>
                                            {n.programName}
                                      </Link>
                                    </TableCell>
                                    <TableCell align="left">{n.description}</TableCell>
                                    <TableCell align="left">{customizedText}</TableCell>
                                    <TableCell align="center">
                                      <div >
                                        { n.active === 0 && <div><CheckCircle htmlColor={statusOk}/></div>}
                                        { n.active === 1 && <div><PauseCircleFilled htmlColor={statusInactive}/></div>}
                                      </div>

                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                      <div style={AppBindStyles.paginationTableFooter}>
                        <TablePagination
                          rowsPerPageOptions={[5, 10, 25]}
                          component="div"
                          count={totalCount}
                          rowsPerPage={this.state.rowsPerPage}
                          page={this.state.page}
                          backIconButtonProps={{
                            'aria-label': 'Previous Page',
                          }}
                          nextIconButtonProps={{
                            'aria-label': 'Next Page',
                          }}
                          onPageChange={this.handleChangePage}
                          onRowsPerPageChange={this.handleChangeRowsPerPage}
                        />                        
                      </div>
                    </div>
                  </React.Fragment>
                )
              }
              return (
                <div>
                  <Typography>Loading...</Typography>
                  <LoadingSpinner/>
                </div>
              );
            }}
          />
        </div>
      );
    }
    else
    {
      return (<Redirect to='/home' />);
    }
  }

  onSnackbarClose() {
    this.setState( {saveSnackbarOpen: false, saveStatus: '', saveMessage: '', saveSnackbarDuration: null });
  }

  createResellerProgram = (vendorAccountId, resellerAccountId) => {

    this.setState({ saveStatus: 'saving', showSpinner: true });

    if (abEnvironment.isSegmentActive()) { window.analytics.track('Vendor assigning new reseller program'); }   

    const { cpId } = this.state;
    
    let theNewResellerProgramId = null;

    ResellerCommProgramCreateMutation.commit(vendorAccountId, resellerAccountId, cpId)
    .then(response => {
      theNewResellerProgramId = response.createresellerprogramnew.uuid;
      return null;
    })
    .then(response => {
      this.setState({ showSpinner: false });
      let replaceUrl = 'resellerprogramedit?resellerPId=' + theNewResellerProgramId  + "&accountId=" + resellerAccountId;
      if (abEnvironment.isSegmentActive()) { window.analytics.track('Vendor reseller program assignment created'); }   
      this.goTo(replaceUrl);
    })
    .catch(error => {
      this.setState({ showSpinner: false, snackbarOpen: true, saveStatus: 'error', saveMessage: 'Unable to save new reseller program.' });
      // this.goBack();
    });
  }

}

export default withStyles(styleSheet)(ResellerCommPrograms);
